
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      title: 'Identify High-Volume Ticket Categories',
      description: 'We analyze your support data to determine where repetitive volume and cost concentration exist.',
      icon: 'ri-search-eye-line'
    },
    {
      number: '02',
      title: 'Design Around Real Support Workflows',
      description: 'The agent is scoped around actual ticket flows, escalation paths, and system dependencies.',
      icon: 'ri-draft-line'
    },
    {
      number: '03',
      title: 'Integrate Into Your Existing Stack',
      description: 'Deployment occurs within your current helpdesk and backend tools without disrupting your team.',
      icon: 'ri-plug-line'
    },
    {
      number: '04',
      title: 'Reduce Volume. Monitor. Improve.',
      description: 'Ticket volume decreases. Human agents focus on complex cases. Performance is monitored and refined over time.',
      icon: 'ri-line-chart-line'
    }
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: stepsRef, getItemStyle } = useStaggerAnimation(steps.length, { threshold: 0.1 });
  const { ref: resultsRef, isVisible: resultsVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="how-it-works" className="bg-white py-16 md:py-28 relative overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 relative z-10">
        <div
          ref={headerRef}
          className="text-center mb-12 md:mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B8985F]/10 mb-5 md:mb-6">
            <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">Process</span>
          </div>
          <h2 className="text-[28px] md:text-[40px] font-bold text-[#1A1A1A] mb-4 md:mb-5">How It Works</h2>
          <p className="text-[15px] md:text-[17px] text-[#777] max-w-[560px] mx-auto">Operational transformation in four stages</p>
        </div>

        <div ref={stepsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          <div className="absolute top-[52px] left-[12.5%] right-[12.5%] h-[2px] bg-gradient-to-r from-[#B8985F]/20 via-[#B8985F]/40 to-[#B8985F]/20 hidden lg:block"></div>

          {steps.map((step, index) => (
            <div key={index} className="relative text-center group" style={getItemStyle(index)}>
              <div className="w-20 h-20 md:w-[104px] md:h-[104px] rounded-2xl bg-[#FAFAF8] border border-[#F0EDE6] flex flex-col items-center justify-center mx-auto mb-5 md:mb-6 group-hover:border-[#B8985F]/40 group-hover:shadow-[0_8px_30px_rgba(184,152,95,0.1)] transition-all duration-300 relative z-10 cursor-pointer">
                <i className={`${step.icon} text-[#B8985F] text-xl md:text-2xl`}></i>
                <span className="text-[10px] md:text-[11px] text-[#B8985F] font-bold mt-1">{step.number}</span>
              </div>
              <h3 className="text-[15px] md:text-[16px] font-semibold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.4]">{step.title}</h3>
              <p className="text-[13px] md:text-[14px] text-[#888] leading-[1.6]">{step.description}</p>
            </div>
          ))}
        </div>

        <div
          ref={resultsRef}
          className="mt-12 md:mt-20 bg-[#FAFAF8] rounded-xl border border-[#F0EDE6] p-6 md:p-8"
          style={{
            opacity: resultsVisible ? 1 : 0,
            transform: resultsVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.1s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.1s',
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 items-center">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-arrow-down-line text-emerald-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-[24px] md:text-[28px] font-bold text-[#1A1A1A]">70%</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-[#888]">Fewer repetitive tickets</p>
            </div>
            <div className="text-center sm:border-x border-t sm:border-t-0 border-[#E8E4DC] pt-6 sm:pt-0">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-timer-flash-line text-[#B8985F] text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-[24px] md:text-[28px] font-bold text-[#1A1A1A]">4s</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-[#888]">Average first response</p>
            </div>
            <div className="text-center border-t sm:border-t-0 border-[#E8E4DC] pt-6 sm:pt-0">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-money-dollar-circle-line text-emerald-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="text-[24px] md:text-[28px] font-bold text-[#1A1A1A]">45%</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-[#888]">Lower cost per ticket</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
